import { inject, Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NavigationEnd, Router } from "@angular/router";
import { SwUpdate } from "@angular/service-worker";
import { filter, interval, merge } from "rxjs";

@Injectable({
	providedIn: "root",
})
export class UpdateService {
	private swUpdate: SwUpdate = inject(SwUpdate);
	private snackbar: MatSnackBar = inject(MatSnackBar);
	private router: Router = inject(Router);

	// Prevent multiple simultaneous reloads
	private updateInProgress = false;
	private readonly UPDATE_IN_PROGRESS_KEY = "app_update_in_progress";
	private readonly UPDATE_TIMESTAMP_KEY = "app_update_timestamp";
	private readonly UPDATE_COOLDOWN_MS = 60000; // 1 minute cooldown between updates

	constructor() {
		// Clear any stale update flags on startup
		this.clearStaleUpdateFlags();

		// Check for updates on navigation, every 5 minutes, and immediately on startup
		const navigationEnd$ = this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
		);
		const periodicCheck$ = interval(5 * 60 * 1000); // 5 minutes

		merge(navigationEnd$, periodicCheck$).subscribe(() => {
			if (
				this.swUpdate.isEnabled &&
				!this.updateInProgress &&
				!this.isUpdateInCooldown()
			) {
				this.swUpdate.checkForUpdate().catch((err) => {
					console.warn("Update check failed:", err);
				});
			}
		});

		// Initial check on startup - delay slightly to avoid race conditions
		if (this.swUpdate.isEnabled && !this.isUpdateInCooldown()) {
			setTimeout(() => {
				this.swUpdate.checkForUpdate().catch((err) => {
					console.warn("Initial update check failed:", err);
				});
			}, 2000);
		}

		// Handle version updates - NOTE: This subscription stays active for the app lifetime
		// to detect multiple deployments during a user's session
		this.swUpdate.versionUpdates.subscribe((evt) => {
			console.log("Version update event:", evt);

			// Only handle VERSION_READY events (actual updates available)
			if (evt.type !== "VERSION_READY") {
				return;
			}

			// Ignore if we're already handling an update or in cooldown
			if (this.updateInProgress || this.isUpdateInCooldown()) {
				console.log("Update ignored - already in progress or in cooldown");
				return;
			}

			this.updateInProgress = true;
			this.setUpdateInProgress();
			console.log("New version ready:", evt);

			// Only show notification and auto-reload on company path
			const isCompanyPath = window.location.hash.startsWith("#/company");

			if (isCompanyPath) {
				// Show notification with dismiss option - no auto-reload
				const snack = this.snackbar.open("Update Available", "Update Now", {
					duration: 30000, // Auto-dismiss after 30 seconds
				});

				snack.onAction().subscribe(() => {
					snack.dismiss();
					this.swUpdate
						.activateUpdate()
						.then(() => {
							window.location.reload();
						})
						.catch((err) => {
							console.error("Failed to activate update:", err);
							this.clearUpdateFlags();
						});
				});

				// When dismissed (either manually or auto after 30s), clear the update flags
				// so the update can be attempted again later
				snack.afterDismissed().subscribe(() => {
					this.clearUpdateFlags();
				});
			} else {
				// Silent update for other paths - activate but don't reload
				// Update will take effect on next navigation or manual refresh
				this.swUpdate
					.activateUpdate()
					.then(() => {
						console.log(
							"App update activated silently. Changes will take effect on next navigation.",
						);
						// Reset flags after silent update so future updates can be detected
						this.updateInProgress = false;
						this.clearUpdateFlags();
					})
					.catch((err) => {
						console.error("Failed to activate silent update:", err);
						this.clearUpdateFlags();
					});
			}
		});

		this.swUpdate.unrecoverable.subscribe((evt) => {
			if (!this.updateInProgress && !this.isUpdateInCooldown()) {
				this.updateInProgress = true;
				this.setUpdateInProgress();
				const snack = this.snackbar.open(
					"Critical Update Required",
					"Reload App",
					{
						duration: 0, // Don't auto-dismiss for critical updates
					},
				);
				snack.onAction().subscribe(() => {
					window.location.reload();
				});
			}
		});

		// Listen for ServiceWorker errors and handle 403 responses
		this.setupServiceWorkerErrorHandler();
	}

	private setupServiceWorkerErrorHandler(): void {
		// Listen for global errors that might indicate ServiceWorker issues
		window.addEventListener("error", (event) => {
			this.handlePotentialServiceWorkerError(event.error, event.message);
		});

		// Listen for unhandled promise rejections from ServiceWorker
		window.addEventListener("unhandledrejection", (event) => {
			this.handlePotentialServiceWorkerError(event.reason, event.reason?.message);
		});
	}

	private handlePotentialServiceWorkerError(_error: any, message: string): void {
		// Check if this is a ServiceWorker fetch error with 403 response
		const isServiceWorkerError =
			message &&
			(message.includes("ServiceWorker") ||
				message.includes("FetchEvent.respondWith") ||
				message.includes("fetchAndCacheOnce"));

		const is403Error =
			message && (message.includes("403") || message.includes("Response not Ok"));

		if (isServiceWorkerError && is403Error) {
			console.warn("ServiceWorker 403 error detected, forcing reload:", message);
			this.forceReload(
				"ServiceWorker encountered a 403 error and needs to reload to fetch the latest resources",
			);
		}
	}

	private forceReload(reason: string): void {
		if (!this.updateInProgress && !this.isUpdateInCooldown()) {
			this.updateInProgress = true;
			this.setUpdateInProgress();
			let userResponded = false;

			const snack = this.snackbar.open(reason, "Reload Now", {
				duration: 5000,
			});

			snack.onAction().subscribe(() => {
				userResponded = true;
				window.location.reload();
			});

			// Auto-reload after 5 seconds if user doesn't respond
			setTimeout(() => {
				if (!userResponded) {
					window.location.reload();
				}
			}, 5000);
		}
	}

	/**
	 * Check if an update is currently in cooldown period
	 */
	private isUpdateInCooldown(): boolean {
		try {
			const timestamp = localStorage.getItem(this.UPDATE_TIMESTAMP_KEY);
			if (!timestamp) {
				return false;
			}

			const lastUpdateTime = parseInt(timestamp, 10);
			const now = Date.now();
			const timeSinceUpdate = now - lastUpdateTime;

			return timeSinceUpdate < this.UPDATE_COOLDOWN_MS;
		} catch (e) {
			console.warn("Failed to check update cooldown:", e);
			return false;
		}
	}

	/**
	 * Set the update in progress flag in localStorage
	 */
	private setUpdateInProgress(): void {
		try {
			localStorage.setItem(this.UPDATE_IN_PROGRESS_KEY, "true");
			localStorage.setItem(this.UPDATE_TIMESTAMP_KEY, Date.now().toString());
		} catch (e) {
			console.warn("Failed to set update in progress flag:", e);
		}
	}

	/**
	 * Clear the update flags
	 */
	private clearUpdateFlags(): void {
		try {
			localStorage.removeItem(this.UPDATE_IN_PROGRESS_KEY);
			this.updateInProgress = false;
		} catch (e) {
			console.warn("Failed to clear update flags:", e);
		}
	}

	/**
	 * Clear stale update flags on startup (flags older than 2 minutes)
	 */
	private clearStaleUpdateFlags(): void {
		try {
			const inProgress = localStorage.getItem(this.UPDATE_IN_PROGRESS_KEY);
			const timestamp = localStorage.getItem(this.UPDATE_TIMESTAMP_KEY);

			if (inProgress && timestamp) {
				const lastUpdateTime = parseInt(timestamp, 10);
				const now = Date.now();
				const timeSinceUpdate = now - lastUpdateTime;

				// If the flag is older than 2 minutes, consider it stale
				if (timeSinceUpdate > 120000) {
					console.log("Clearing stale update flags");
					localStorage.removeItem(this.UPDATE_IN_PROGRESS_KEY);
				}
			}
		} catch (e) {
			console.warn("Failed to clear stale update flags:", e);
		}
	}
}
